import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { DiamondAlert } from '@/shared/components/DiamondAlertIcon';
import { formatDateRange } from '@/shared/format/date-format';
import { CalendarHoverCardBody } from './CalendarHoverCard';
import {
  BAR_HEIGHT_PX,
  STATUS_WORD_KEYS,
  SUB_ROW_HEIGHT_PX,
  barTitleColour,
  effectiveBarFill,
  hatchCss,
  renderedBarFill,
  statusCapsuleColours,
  statusLabelKey,
  type PlacedBar,
} from './lane-layer';
import type { RovingItem } from './use-roving-calendar-focus';

/**
 * One Initiative bar in a Platform lane (UI spec §4.2, task 22.3).
 *
 * Anatomy is §4.2's: 26px tall, 13px radius, white 10.5px text with
 * ellipsis overflow, a 1px `rgba(0,0,0,.12)` border, filled with the Platform
 * colour, and `brightness(1.08)` + a 2px `--navy` outline on hover and focus.
 *
 * ## Status is a word plus a fill treatment, not an icon
 *
 * Status is carried by the **status word** in a leading capsule, reinforced by the
 * **fill treatment** — the three pre-approval statuses share a pale tint and are
 * marked by a dashed outline (Draft), nothing (Submitted) or a very soft hatch
 * (Planned), while Approved onward are solid — and by the remainder of what
 * {@link PlacedBar.statusStyle} resolves as pure data: opacity, the Final inset
 * border, and the Cancelled grey override with its strikethrough. All six statuses
 * of R9.1 are encoded in one place and this component only maps that data onto CSS.
 * See `lane-layer.ts` for why the six-icon set was replaced and why status still
 * takes no colour of its own.
 *
 * The word degrades with the bar's measured width (`full` → `short` → `letter`,
 * {@link PlacedBar.labelTier}) so it never truncates the title into
 * meaninglessness and never vanishes entirely.
 *
 * Two details keep the word legible *over* the texture, which is the real risk of
 * running both encodings at once: the capsule takes an **opaque, maximum-contrast**
 * background from {@link statusCapsuleColours}, so it occludes the texture entirely
 * and its own contrast does not depend on the Platform colour beneath it; and both
 * the title's ink and the capsule are chosen against {@link renderedBarFill}, which
 * compounds the unfilled tint, the texture's mean coverage *and* the status fade, so
 * a textured *and* faded bar cannot slip below the 4.5:1 minimum (R18.7) the way the
 * old `.72` opacity value did.
 *
 * ## Non-colour encoding (R10.5, R10.6, R18.8)
 *
 * The Platform fill is never the only carrier of meaning. Every bar shows the
 * status word as text and its status texture; a confidential Initiative is prefixed
 * by a real `Lock` icon and an elevated-priority one by a real {@link DiamondAlert}
 * icon — an exclamation in a diamond, replacing the `Star` that read as
 * "favourite" rather than "needs attention" (Lucide components, not Unicode
 * glyphs, per §9's accessibility-gap list); and a clipped bar shows a `‹`/`›`
 * continuation marker (R10.14).
 *
 * ## Accessibility (§9)
 *
 * The bar is a real `<button>`, not a `div` with a click handler, and it carries
 * a full accessible name. Its tooltip is a real accessible tooltip component
 * (Radix, via the shadcn wrapper), available on both pointer hover and keyboard
 * focus — never a `title=` attribute. The full untruncated Title, the Sales_Cycle
 * membership, status, priority and confidentiality all live in the tooltip and,
 * later, the Detail_Panel; the bar itself truncates.
 *
 * The bar is one item of the roving-tabindex composite widget (task 22.4): the
 * {@link InitiativeBarProps.roving} props carry its `tabindex` (0 only when it
 * holds the roving focus, -1 otherwise), its ref for the controller to focus, and
 * the arrow/Enter/Space `keydown` handler. Pointer clicks still open the
 * Detail_Panel directly (R10.13); keyboard Enter/Space open it through the roving
 * handler, which suppresses the button's synthesized click so the panel opens
 * exactly once.
 */
export interface InitiativeBarProps {
  readonly placed: PlacedBar;
  /** The Platform colour fill (R10.5); the status treatment may override it. */
  readonly platformColour: string;
  /**
   * Whether the bar's priority is elevated (R10.8); renders the
   * {@link DiamondAlert} prefix.
   */
  readonly elevatedPriority: boolean;
  /** Opens the Detail_Panel for this Initiative on pointer activation (R10.13). */
  readonly onOpen: (initiativeId: string) => void;
  /** Roving-tabindex props from the composite-widget controller (task 22.4). */
  readonly roving: RovingItem;
  /**
   * Vertical offset (px) pushing the bar below the lane's milestone swimlane
   * strip, if any. `0` (the default) for a lane with no milestones (task:
   * milestones-per-platform).
   */
  readonly verticalOffsetPx?: number | undefined;
  /**
   * Whether this bar is the one the lane's swimlane is currently wiring to
   * (a milestone is hovered/focused). Renders the shared highlight outline so the
   * milestone and its Initiative light up together.
   */
  readonly highlighted?: boolean | undefined;
}

export function InitiativeBar({
  placed,
  platformColour,
  elevatedPriority,
  onOpen,
  roving,
  verticalOffsetPx = 0,
  highlighted = false,
}: InitiativeBarProps): React.JSX.Element {
  const { t } = useTranslation();
  const { bar, statusStyle } = placed;
  const fill = statusStyle.colourOverride ?? platformColour;
  // Chosen against the fill the bar actually paints — Platform colour *plus* the
  // hatch's mean lightening — so neither a pale Platform colour nor a dense texture
  // leaves an unreadable title (R18.7). An explicit status override still wins,
  // which keeps Cancelled's hand-picked value.
  // The colour actually painted: the pale pre-approval tint lightens the Platform
  // colour before anything else sees it.
  const paintedFill = effectiveBarFill(fill, statusStyle.fillLightenAlpha);
  // And the colour the eye finally receives, with the hatch's mean coverage and the
  // status fade composited in too — what every contrast choice below is judged
  // against, because those three terms compound.
  const effectiveFill = renderedBarFill(platformColour, statusStyle);
  // Planned's hatch is drawn in the Platform colour, so its CSS cannot exist until
  // here; the legend swatch builds its own from the same spec.
  const hatchImage =
    statusStyle.hatch === null ? 'none' : hatchCss(statusStyle.hatch, platformColour);
  const titleColour = statusStyle.textColour ?? barTitleColour(effectiveFill);
  const topPx = placed.lane * SUB_ROW_HEIGHT_PX + verticalOffsetPx;
  const statusLabel = t(statusLabelKey(bar.status, placed.labelTier));
  // An opaque, maximum-contrast chip rather than a translucent wash, so the
  // capsule's legibility does not depend on the Platform colour beneath it.
  const capsule = statusCapsuleColours(effectiveFill);
  // Draft's outline is the encoding, so it takes the Platform colour; a hairline
  // would vanish against the pale fill.
  const borderColour = statusStyle.borderUsesPlatformColour
    ? platformColour
    : 'rgba(0,0,0,0.12)';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          // Pointer activation opens the panel directly; keyboard Enter/Space open
          // it through the roving controller (task 22.4, R10.13).
          onClick={() => onOpen(bar.id)}
          ref={roving.ref}
          tabIndex={roving.tabIndex}
          onKeyDown={roving.onKeyDown}
          onFocus={roving.onFocus}
          aria-label={accessibleName(placed, elevatedPriority, t)}
          className={cn(
            'group absolute flex items-center gap-1 overflow-hidden rounded-[13px]',
            'px-[6px] text-left text-[10.5px] font-semibold',
            'transition-[filter,outline] outline-none',
            'hover:brightness-[1.08] hover:outline hover:outline-2 hover:outline-[var(--navy)]',
            'focus-visible:brightness-[1.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--navy)]',
          )}
          style={{
            left: `calc(${String(placed.leftPercent)}% + 2px)`,
            width: `calc(max(${String(placed.widthPercent)}%, 24px) - 4px)`,
            top: `${String(topPx + (SUB_ROW_HEIGHT_PX - BAR_HEIGHT_PX) / 2)}px`,
            height: `${String(BAR_HEIGHT_PX)}px`,
            backgroundColor: paintedFill,
            // Planned's soft hatch, composited over the fill. `none` rather than
            // omitted so an unhatched status explicitly clears any inherited image.
            backgroundImage: hatchImage,
            // Draft's dashed Platform outline versus the default hairline (§4.2).
            borderStyle: statusStyle.borderStyle,
            borderWidth: `${String(statusStyle.borderWidthPx)}px`,
            borderColor: borderColour,
            opacity: statusStyle.opacity,
            color: titleColour,
            // The Final inset border and the swimlane highlight outline can both
            // apply; compose them so neither is lost when a milestone wires to a
            // Final bar.
            boxShadow: [statusStyle.insetBorder, highlighted ? '0 0 0 2px var(--navy)' : null]
              .filter((shadow): shadow is string => shadow !== null)
              .join(', ') || undefined,
            filter: highlighted ? 'brightness(1.08)' : undefined,
          }}
        >
          {placed.geometry.continuesBefore ? (
            <span aria-hidden className="shrink-0 leading-none">
              ‹
            </span>
          ) : null}
          {/* The status capsule leads the bar's own content, ahead of the
              confidential and priority markers, because it is the one marker every
              bar carries — a consistent first element is what makes a lane scannable
              by status.

              Its background is opaque and picked per bar for maximum contrast
              (an inverted chip: white-on-navy, dark-on-pale), so the word is
              legible whatever Platform colour sits beneath it and the hatch is
              occluded outright rather than showing through.

              `aria-hidden`: the status is already in the accessible name below, so
              exposing it here would state it twice. */}
          <span
            aria-hidden
            className="shrink-0 rounded-[9px] px-[5px] py-[1px] text-[9px] leading-[15px] font-bold tracking-[0.05em] uppercase"
            style={{ backgroundColor: capsule.background, color: capsule.text }}
          >
            {statusLabel}
          </span>
          {bar.isConfidential ? (
            <Lock aria-hidden className="size-[11px] shrink-0" strokeWidth={2.5} />
          ) : null}
          {elevatedPriority ? (
            // Outlined, not `fill-current`: the fill would swallow the exclamation
            // the glyph carries its meaning in. 15px rather than the 11px the other
            // markers take — the exclamation inside the diamond needs the room to
            // stay legible, and this is the one marker meant to be noticed first.
            <DiamondAlert aria-hidden className="size-[15px] shrink-0" strokeWidth={2.5} />
          ) : null}
          <span
            className={cn('truncate', statusStyle.strikethrough && 'line-through')}
          >
            {bar.title}
          </span>
          {placed.geometry.continuesAfter ? (
            <span aria-hidden className="ml-auto shrink-0 leading-none">
              ›
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      {/* The `panel` surface: the same floating card the milestone hover and the
          milestone-cluster popover use, so every Calendar hover surface matches. */}
      <TooltipContent side="top" variant="panel" sideOffset={6}>
        <BarTooltipBody placed={placed} elevatedPriority={elevatedPriority} swatchColour={fill} />
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The tooltip body: title, Sales_Cycle, status, priority, confidentiality (§4.2).
 *
 * Exported for the non-colour encoding test, which asserts the card's wording
 * directly: opening the real Radix tooltip in jsdom drives its popper and timers
 * and is far too slow to run per case. This is the component the tooltip renders,
 * so the assertions still read the card a user sees.
 */
export function BarTooltipBody({
  placed,
  elevatedPriority,
  swatchColour,
}: {
  readonly placed: PlacedBar;
  readonly elevatedPriority: boolean;
  /** The bar's rendered fill, echoed as a decorative swatch in the card heading. */
  readonly swatchColour: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { bar } = placed;
  const dates = renderedDateRange(placed);
  return (
    <CalendarHoverCardBody
      title={bar.title}
      icon={
        // Decorative only: the heading states the title and the lines below state
        // the status in words, so the swatch never carries meaning alone (R10.5).
        <span
          aria-hidden
          className="block size-[8px] rounded-[2px]"
          style={{ background: swatchColour }}
        />
      }
    >
      {/* The Initiative reference is an internal id and is deliberately not shown
          here; the heading names the Initiative and the Detail_Panel carries the
          reference for anyone who needs to quote it. */}
      <span>{t('bar.status', { status: t(STATUS_WORD_KEYS[bar.status]) })}</span>
      {bar.salesCycleIds.length > 0 ? (
        <span>{t('bar.salesCycles', { count: bar.salesCycleIds.length })}</span>
      ) : (
        <span>{t('bar.noSalesCycle')}</span>
      )}
      <span>{t('bar.priority', { priority: priorityWord(bar, elevatedPriority, t) })}</span>
      {bar.isConfidential ? <span>{t('bar.confidential')}</span> : null}
      <span>{dates}</span>
      {placed.geometry.continuesBefore || placed.geometry.continuesAfter ? (
        <span>{t('bar.continuesBeyondYear')}</span>
      ) : null}
    </CalendarHoverCardBody>
  );
}

/**
 * How the bar states its priority: the value's own name, marked when elevated
 * (R10.6, R10.8).
 *
 * The name is resolved server-side onto the bar, so "Low" and "High" are
 * distinguishable here — they previously both read "Normal", which reported the
 * Elevated_Priority_Flag rather than the priority. `priorityName` is `null` only
 * for a payload cached by a build predating that field; the elevated/not wording
 * is kept as the fallback for exactly that case.
 */
function priorityWord(bar: PlacedBar['bar'], elevatedPriority: boolean, t: TFunction): string {
  if (bar.priorityName === null) {
    return elevatedPriority ? t('bar.priorityElevated') : t('bar.priorityNormal');
  }
  return elevatedPriority
    ? t('bar.priorityValueElevated', { priority: bar.priorityName })
    : bar.priorityName;
}

/** A plain accessible name for the bar's `<button>` (§9 "accessible names"). */
function accessibleName(placed: PlacedBar, elevatedPriority: boolean, t: TFunction): string {
  const { bar } = placed;
  const base = t('bar.accessibleName', {
    title: bar.title,
    status: t(STATUS_WORD_KEYS[bar.status]),
  });
  const parts = [
    base,
    // The priority value by name, so a screen reader conveys which priority the
    // bar carries and not only that it is elevated (R10.6).
    bar.priorityName === null ? null : t('bar.accessiblePriority', { priority: bar.priorityName }),
    elevatedPriority ? t('bar.accessibleElevated') : null,
    bar.isConfidential ? t('bar.accessibleConfidential') : null,
  ].filter((part): part is string => part !== null);
  return parts.join(', ');
}

/** The stored (unclipped) date range shown in the tooltip, `dd.MM.yyyy` (R10.14, R18.10). */
function renderedDateRange(placed: PlacedBar): string {
  return formatDateRange(placed.geometry.storedStartDate, placed.geometry.storedEndDate);
}
